import { useState } from 'react';
import { ActionIcon, Container, Group, Textarea } from '@mantine/core';
import { IconArrowUp } from '@tabler/icons-react';
import { chatContainerSize, Cmp } from '~portal/lib';
import { touchTarget } from '~portal/styles/touch-target.css';
import * as actionStyles from './action-icons.css';
import * as styles from './ChatInput.css';
import { ChatModeMenu } from './ChatModeMenu';
import { useChatInput } from './useChatInput';

export type ChatInputProps = {
  onSubmit?: (val: string) => void;
  disabled?: boolean;
  /** Whether the conversation already has messages — drives the placeholder. */
  hasMessages?: boolean;
  /**
   * Whether the chat hasn't been created yet (the new-chat composer). The
   * Answer/Analyse mode dropdown only makes sense before the first message
   * fixes the conversation's mode — once a conversation exists it disappears
   * entirely (Charlie rev 2 follow-up, H2-181).
   */
  isNewChat?: boolean;
};

/**
 * On a touch screen, focusing the composer raises the on-screen keyboard and iOS scrolls the page
 * to make room — on first load that pushed the mode picker out of view (H2-238). A tap on the
 * composer is one deliberate step there; a desktop pointer keeps typing-ready focus.
 */
const shouldAutoFocus = () => !window.matchMedia('(pointer: coarse)').matches;

export const ChatInput: Cmp<ChatInputProps> = function ({
  onSubmit,
  disabled,
  hasMessages,
  isNewChat,
}) {
  const { input, setInput, handleSubmit, handleKeyDown, isSubmitDisabled, placeholder } =
    useChatInput({ onSubmit, disabled, hasMessages });
  // Read once: autofocus only acts on mount, so a later pointer change has nothing to update.
  const [autoFocus] = useState(shouldAutoFocus);

  return (
    <Container size={chatContainerSize} className={styles.inputContainer}>
      <Group gap="xs" className={styles.inputGroup} wrap="nowrap" align="flex-end">
        <Textarea
          variant="unstyled"
          size="md"
          placeholder={placeholder}
          autosize
          minRows={1}
          maxRows={12}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- opt-in by the caller for the composer of a fresh chat, the page's one input and the reason it was opened
          autoFocus={autoFocus}
          className={styles.textarea}
          classNames={{ input: styles.composerLine }}
        />
        <Group gap={4} wrap="nowrap" className={styles.trailingControls}>
          {isNewChat && <ChatModeMenu disabled={disabled} />}
          <ActionIcon
            size={styles.sendButtonSize}
            radius="xl"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            // A turn in flight is its own state, not "nothing to send": the composer is empty
            // then too, so a merely disabled arrow was indistinguishable from idle (H2-237).
            // `loading` swaps the arrow for a spinner and keeps the button disabled.
            loading={!!disabled}
            aria-busy={!!disabled}
            variant="filled"
            className={`${actionStyles.sendButton} ${touchTarget}`}
            aria-label="Send message"
            data-dd-action-name="Send prompt"
          >
            <IconArrowUp size={22} />
          </ActionIcon>
        </Group>
      </Group>
    </Container>
  );
};
